import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, MessageSquare, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/voice-vs-chatbot.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const AIVoiceAgentVsChatbot = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.aiVoiceAgentVsChatbot.metaTitle')}</title>
        <meta name="description" content={t('blog.aiVoiceAgentVsChatbot.metaDescription')} />
        <meta name="keywords" content="AI voice agent vs chatbot, voice AI vs text chatbot, phone AI vs chat AI, conversational AI comparison, best AI for customer service" />
        <link rel="canonical" href="https://call24x7.ai/blog/ai-voice-agent-vs-chatbot" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7 Team" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/ai-voice-agent-vs-chatbot" />
        <meta property="og:title" content={t('blog.aiVoiceAgentVsChatbot.title')} />
        <meta property="og:description" content={t('blog.aiVoiceAgentVsChatbot.metaDescription')} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.aiVoiceAgentVsChatbot.title'),
            "description": t('blog.aiVoiceAgentVsChatbot.metaDescription'),
            "author": { "@type": "Organization", "name": "Call24x7.AI" },
            "publisher": { "@type": "Organization", "name": "Call24x7.AI" },
            "datePublished": "2025-04-10",
            "dateModified": "2025-04-10"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.aiVoiceAgentVsChatbot.title')}
        category={t('blog.categories.comparison')}
        date="April 10, 2025"
        readTime={`7 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI voice agent vs chatbot comparison"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.aiVoiceAgentVsChatbot.intro')}
        </p>

        <div className="grid md:grid-cols-2 gap-4 my-8">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Phone className="w-12 h-12 text-primary mx-auto mb-3" />
              <h3 className="text-xl font-bold">{t('blog.aiVoiceAgentVsChatbot.voiceAgent')}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t('blog.aiVoiceAgentVsChatbot.voiceAgentDesc')}</p>
            </CardContent>
          </Card>
          <Card className="bg-secondary/5 border-secondary/20">
            <CardContent className="pt-6 text-center">
              <MessageSquare className="w-12 h-12 text-secondary mx-auto mb-3" />
              <h3 className="text-xl font-bold">{t('blog.aiVoiceAgentVsChatbot.chatbot')}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t('blog.aiVoiceAgentVsChatbot.chatbotDesc')}</p>
            </CardContent>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceAgentVsChatbot.comparisonTitle')}</h2>
        <div className="overflow-x-auto my-6">
          <table className="min-w-full border border-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-2 text-left">{t('blog.aiVoiceAgentVsChatbot.factor')}</th>
                <th className="px-4 py-2 text-left">{t('blog.aiVoiceAgentVsChatbot.voiceAgent')}</th>
                <th className="px-4 py-2 text-left">{t('blog.aiVoiceAgentVsChatbot.chatbot')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="px-4 py-2 font-semibold">{t('blog.aiVoiceAgentVsChatbot.channel')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.channelVoice')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.channelChat')}</td>
              </tr>
              <tr className="border-t">
                <td className="px-4 py-2 font-semibold">{t('blog.aiVoiceAgentVsChatbot.speed')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.speedVoice')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.speedChat')}</td>
              </tr>
              <tr className="border-t">
                <td className="px-4 py-2 font-semibold">{t('blog.aiVoiceAgentVsChatbot.complexity')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.complexityVoice')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.complexityChat')}</td>
              </tr>
              <tr className="border-t">
                <td className="px-4 py-2 font-semibold">{t('blog.aiVoiceAgentVsChatbot.customerPref')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.prefVoice')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.prefChat')}</td>
              </tr>
              <tr className="border-t">
                <td className="px-4 py-2 font-semibold">{t('blog.aiVoiceAgentVsChatbot.costPerInteraction')}</td>
                <td className="px-4 py-2">$0.15-0.50</td>
                <td className="px-4 py-2">$0.01-0.10</td>
              </tr>
              <tr className="border-t">
                <td className="px-4 py-2 font-semibold">{t('blog.aiVoiceAgentVsChatbot.setupDifficulty')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.setupVoice')}</td>
                <td className="px-4 py-2">{t('blog.aiVoiceAgentVsChatbot.setupChat')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceAgentVsChatbot.whenVoiceTitle')}</h2>
        <ul className="space-y-3 my-4">
          {[
            t('blog.aiVoiceAgentVsChatbot.whenVoice1'),
            t('blog.aiVoiceAgentVsChatbot.whenVoice2'),
            t('blog.aiVoiceAgentVsChatbot.whenVoice3'),
            t('blog.aiVoiceAgentVsChatbot.whenVoice4'),
            t('blog.aiVoiceAgentVsChatbot.whenVoice5')
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceAgentVsChatbot.whenChatTitle')}</h2>
        <ul className="space-y-3 my-4">
          {[
            t('blog.aiVoiceAgentVsChatbot.whenChat1'),
            t('blog.aiVoiceAgentVsChatbot.whenChat2'),
            t('blog.aiVoiceAgentVsChatbot.whenChat3'),
            t('blog.aiVoiceAgentVsChatbot.whenChat4'),
            t('blog.aiVoiceAgentVsChatbot.whenChat5')
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceAgentVsChatbot.bothTitle')}</h2>
        <p>{t('blog.aiVoiceAgentVsChatbot.bothIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li><strong>{t('blog.aiVoiceAgentVsChatbot.bothChat')}:</strong> {t('blog.aiVoiceAgentVsChatbot.bothChatDesc')}</li>
          <li><strong>{t('blog.aiVoiceAgentVsChatbot.bothVoice')}:</strong> {t('blog.aiVoiceAgentVsChatbot.bothVoiceDesc')}</li>
          <li><strong>{t('blog.aiVoiceAgentVsChatbot.bothHandoff')}:</strong> {t('blog.aiVoiceAgentVsChatbot.bothHandoffDesc')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceAgentVsChatbot.costTitle')}</h2>
        <Card className="bg-muted/50 my-6">
          <CardContent className="pt-6">
            <p className="font-semibold mb-4">{t('blog.aiVoiceAgentVsChatbot.costIntro')}</p>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>{t('blog.aiVoiceAgentVsChatbot.voiceAgent')}</span>
                <span className="font-bold">$150-500</span>
              </div>
              <div className="flex justify-between">
                <span>{t('blog.aiVoiceAgentVsChatbot.chatbot')}</span>
                <span className="font-bold">$50-150</span>
              </div>
              <div className="flex justify-between">
                <span>{t('blog.aiVoiceAgentVsChatbot.humanComparison')}</span>
                <span className="font-bold">$2,000-5,000</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceAgentVsChatbot.satisfactionTitle')}</h2>
        <p>{t('blog.aiVoiceAgentVsChatbot.satisfactionIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li><strong>{t('blog.aiVoiceAgentVsChatbot.stat1')}</strong></li>
          <li><strong>{t('blog.aiVoiceAgentVsChatbot.stat2')}</strong></li>
          <li><strong>{t('blog.aiVoiceAgentVsChatbot.stat3')}</strong></li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiVoiceAgentVsChatbot.bottomLineTitle')}</h2>
        <p>{t('blog.aiVoiceAgentVsChatbot.bottomLineDesc')}</p>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.aiVoiceAgentVsChatbot.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.aiVoiceAgentVsChatbot.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.aiVoiceAgentVsChatbot.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default AIVoiceAgentVsChatbot;
